import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import usePageTitle from "hooks/usePageTitle";
import { Joyride } from "react-joyride";
import { getJoyrideScrollOffset } from "utils/joyrideConfig";
import { useManagementTour } from "hooks/useManagementTour";
import PageMenu from "../../../components/blocks/PageMenu/PageMenu";
import GeneralManagement from "../GeneralManagement/GeneralManagement";
import { useManagementFormState } from "../GeneralManagement/ManagementFormStateContext";
import { useGeneralManagementState } from "../GeneralManagement/generalManagementState";
import ManagementAuthorizations from "../ManagementAuthorizations/ManagementAuthorizations";
import useDecode from "../../../hooks/useDecode";
import GeneralDocuments from "../generalDocuments/GeneralDocuments";
import "./Management.scss";

type SectionId =
  | "gestiones-activa"
  | "gestiones-generales"
  | "formularios-autorizaciones"
  | "documentos-autorizaciones"
  | "documentos-generales";

const Management: React.FC = () => {
  const navigate = useNavigate();
  usePageTitle("Mis Gestiones");
  const tokenResponse = useDecode("token");
  const breadcrumbs = ["Mis gestiones"];
  const roles = tokenResponse?.extension_roles;
  const rolesArray = JSON.parse(roles && roles);
  const isAutonomo = rolesArray.includes("autonomo");

  const myState = useManagementFormState();
  const { state } = useGeneralManagementState();
  const [activeSection, setActiveSection] =
    useState<SectionId>("gestiones-activa");
  const [docCount, setDocCount] = useState<number>(1);

  const { run, tourKey, steps, handleCallback, startTour } =
    useManagementTour();

  useEffect(() => {
    const handler = () => startTour();
    window.addEventListener("solimat:startManagementTour", handler);
    return () =>
      window.removeEventListener("solimat:startManagementTour", handler);
  }, [startTour]);

  const JOYRIDE_PROPS = {
    continuous: true,
    scrollToFirstStep: true,
    locale: {
      back: "Anterior",
      close: "Cerrar",
      last: "Finalizar",
      next: "Siguiente",
      nextWithProgress: "Siguiente ({current} de {total})",
      open: "Iniciar tour de ayuda",
      skip: "Omitir tour",
    },
    options: {
      primaryColor: "#0F1F3D",
      zIndex: 10000,
      overlayColor: "rgba(15,47,73,0.45)",
      showProgress: true,
      buttons: ["back", "skip", "close", "primary"],
    } as any,
    styles: {
      beaconInner: { backgroundColor: "#F97316" },
      beaconOuter: {
        borderColor: "#F97316",
        backgroundColor: "rgba(249,115,22,0.2)",
      },
      tooltip: { borderRadius: 8, fontSize: 14 },
      tooltipTitle: {
        fontSize: 15,
        fontWeight: 700,
        textAlign: "left" as const,
      },
      tooltipContent: { textAlign: "left" as const },
    },
  };

  const handleOnClick = (title: string, url: string) => {
    myState.dispatch({ type: "SET_URL_FORM", payload: url });
    myState.dispatch({ type: "SET_TITLE_FORM", payload: title });
    const nameFormModified = title
      .normalize("NFD")
      .replace(/([aeio])́|(u)[́̈]/gi, "$1$2")
      .normalize()
      .replace(/ /g, "-");
    navigate(`/mis-gestiones/${nameFormModified}/form`);
  };

  // Counts leídos del estado global (poblado por MainLayout al arrancar la app)
  const prestacionActiva = state?.prestacionActiva ?? [];
  const prestacionGeneral = state?.prestacionGeneral ?? [];
  const gruposActiva = state?.gruposPrestacionActiva ?? [];
  const gruposGeneral = state?.gruposPrestacionGeneral ?? [];
  const formsAutorizaciones = state?.formulariosAutorizaciones ?? [];
  const docsAutorizaciones = state?.documentosAutorizaciones ?? [];

  const countActiva =
    prestacionActiva.length +
    gruposActiva.reduce(
      (s: number, g: any) => s + (g.Gestiones?.length ?? 0),
      0,
    );
  const countGeneral =
    prestacionGeneral.length +
    gruposGeneral.reduce(
      (s: number, g: any) => s + (g.Gestiones?.length ?? 0),
      0,
    );
  const countForms = formsAutorizaciones.length;
  const countDocs = docsAutorizaciones.length;

  const NAV_GROUPS = [
    {
      label: "Trámites disponibles",
      items: [
        {
          id: "gestiones-activa" as SectionId,
          label: "Gestiones de la prestación activa",
          count: countActiva,
        },
        {
          id: "gestiones-generales" as SectionId,
          label: "Gestiones generales",
          count: countGeneral,
        },
      ],
    },
    ...(isAutonomo
      ? [
          {
            label: "Autorizaciones",
            items: [
              {
                id: "formularios-autorizaciones" as SectionId,
                label: "Formularios Autorizaciones",
                count: countForms,
              },
              {
                id: "documentos-autorizaciones" as SectionId,
                label: "Documentos Autorizaciones",
                count: countDocs,
              },
            ],
          },
        ]
      : []),
    {
      label: "Archivos y Descargas",
      items: [
        {
          id: "documentos-generales" as SectionId,
          label: "Documentos Generales",
          count: docCount,
        },
      ],
    },
  ];

  // Label of the currently active item (for content heading)
  const activeLabel =
    NAV_GROUPS.flatMap((g) => g.items).find((i) => i.id === activeSection)
      ?.label ?? "";

  return (
    <>
      <Joyride
        key={tourKey}
        steps={steps}
        run={run}
        onEvent={handleCallback}
        {...JOYRIDE_PROPS}
        options={{
          ...JOYRIDE_PROPS.options,
          scrollOffset: getJoyrideScrollOffset(),
        }}
      />
      <div className="container">
        <PageMenu
          title="Mis Gestiones"
          breadcrumbs={breadcrumbs}
          subtitle="Trámites, autorizaciones y documentos a tu disposición"
        />

        <div className="mgmt-layout">
          {/* ── Sidebar ────────────────────────────────── */}
          <aside className="mgmt-sidebar">
            <nav aria-label="Secciones de gestiones">
              {NAV_GROUPS.map((group) => (
                <div key={group.label} className="mgmt-sidebar__group">
                  <span className="mgmt-sidebar__group-label">
                    {group.label}
                  </span>
                  {group.items.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`mgmt-sidebar__item${activeSection === item.id ? " is-active" : ""}`}
                      onClick={() => setActiveSection(item.id)}
                    >
                      <span className="mgmt-sidebar__item-label">
                        {item.label}
                      </span>
                      {item.count > 0 && (
                        <span className="mgmt-sidebar__item-count">
                          {item.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              ))}
            </nav>
          </aside>

          {/* ── Content ────────────────────────────────── */}
          <main className="mgmt-content">
            <div className="mgmt-content__head">
              <h2 className="mgmt-content__title">{activeLabel}</h2>
            </div>

            {/* GeneralManagement: shows only the active subsection */}
            {(activeSection === "gestiones-activa" ||
              activeSection === "gestiones-generales") && (
              <GeneralManagement
                handleOnClick={handleOnClick}
                subsection={
                  activeSection === "gestiones-activa" ? "activa" : "general"
                }
              />
            )}

            {/* ManagementAuthorizations: always mounts (for data fetching),
              renders content only when its subsection is active */}
            {isAutonomo && (
              <ManagementAuthorizations
                handleOnClick={handleOnClick}
                subsection={
                  activeSection === "formularios-autorizaciones"
                    ? "forms"
                    : activeSection === "documentos-autorizaciones"
                      ? "docs"
                      : null
                }
              />
            )}

            {<GeneralDocuments onCountReady={setDocCount} showData={activeSection === "documentos-generales" } />}
          </main>
        </div>
      </div>
    </>
  );
};

export default Management;
